import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { format } from 'date-fns';
import {
  Shield,
  Smartphone,
  Trash2,
  Check,
  AlertTriangle,
  Bell,
  Mail,
  Loader2,
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { TwoFactorSetup } from '@/components/auth/TwoFactorSetup';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export function SecuritySection() {
  const [showSetup, setShowSetup] = useState(false);
  const queryClient = useQueryClient();

  const { data: factors, isLoading } = useQuery({
    queryKey: ['mfa-factors'],
    queryFn: async () => {
      const { data, error } = await supabase.auth.mfa.listFactors();
      if (error) throw error;
      return data;
    },
  });

  const unenrollMutation = useMutation({
    mutationFn: async (factorId: string) => {
      const { error } = await supabase.auth.mfa.unenroll({ factorId });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['mfa-factors'] });
      toast.success('Two-factor authentication disabled');
    },
    onError: () => {
      toast.error('Failed to disable 2FA');
    },
  });

  const verifiedFactors = factors?.totp?.filter((f) => f.status === 'verified') || [];
  const has2FA = verifiedFactors.length > 0;

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5" />
            Account Security
          </CardTitle>
          <CardDescription>
            Manage two-factor authentication and security alerts
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* 2FA Section */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${has2FA ? 'bg-green-100 dark:bg-green-900/30' : 'bg-muted'}`}>
                  <Smartphone className={`h-5 w-5 ${has2FA ? 'text-green-600' : 'text-muted-foreground'}`} />
                </div>
                <div>
                  <h4 className="font-medium">Two-Factor Authentication</h4>
                  <p className="text-sm text-muted-foreground">
                    {has2FA
                      ? 'Your account is protected with 2FA'
                      : 'Add an extra layer of security'}
                  </p>
                </div>
              </div>
              {has2FA ? (
                <Badge variant="default" className="bg-green-600">
                  <Check className="h-3 w-3 mr-1" />
                  Enabled
                </Badge>
              ) : (
                <Button onClick={() => setShowSetup(true)}>
                  Enable 2FA
                </Button>
              )}
            </div>

            {has2FA && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                className="pl-12 space-y-2"
              >
                {verifiedFactors.map((factor) => (
                  <div
                    key={factor.id}
                    className="flex items-center justify-between p-3 rounded-lg bg-muted"
                  >
                    <div>
                      <p className="font-medium text-sm">{factor.friendly_name || 'Authenticator App'}</p>
                      <p className="text-xs text-muted-foreground">
                        Added {format(new Date(factor.created_at), 'MMM d, yyyy')}
                      </p>
                    </div>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="ghost" size="sm" className="text-destructive hover:text-destructive">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle className="flex items-center gap-2">
                            <AlertTriangle className="h-5 w-5 text-destructive" />
                            Disable Two-Factor Authentication?
                          </AlertDialogTitle>
                          <AlertDialogDescription>
                            This will remove 2FA protection from your account. You'll only need your
                            password to sign in, which is less secure.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction
                            onClick={() => unenrollMutation.mutate(factor.id)}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                          >
                            {unenrollMutation.isPending ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              'Disable 2FA'
                            )}
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                ))}
              </motion.div>
            )}
          </div>
        </CardContent>
      </Card>

      <TwoFactorSetup
        open={showSetup}
        onOpenChange={setShowSetup}
        onSuccess={() => {
          queryClient.invalidateQueries({ queryKey: ['mfa-factors'] });
        }}
      />
    </>
  );
}
